import React, {FC, useMemo} from "react";
import {MailOutlined} from "@ant-design/icons";
import {Button, Modal, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {StatusTag} from "../../../Components/ui";
import type {StatusTone} from "../../../Components/ui";
import {formatLocalDateTime} from "../adminDateFormat";
import {EMPTY_OPERATIONS_VALUE, operationalRegistrationCodeStatusTone} from "../participantOperationsHelpers";
import {dateSorter, sortText, textSorter} from "../tableSorters";
import {emailDeliveryTone} from "./participantDetailHelpers";
import type {ParticipantRegistration, ParticipantRegistrationCode, QrInvitationState} from "./participantDetailTypes";
import {DetailPanel, EnumTag, useEmptyText, useEnumLabel} from "./participantDetailUi";
import ParticipantInvitationQrImage from "./ParticipantInvitationQrImage";
import ParticipantRegistrationCodeText from "./ParticipantRegistrationCodeText";
import type {ParticipantDetailModel} from "./useParticipantDetail";

const qrInvitationTone = (state?: QrInvitationState): StatusTone =>
    state?.showActiveTag ? "success" : state?.showEligibleTag ? "info" : "neutral";

export const QrInvitationStateTag: FC<{state?: QrInvitationState}> = ({state}) => {
    const {t} = useTranslation();

    return (
        <StatusTag tone={qrInvitationTone(state)}>
            {state?.showActiveTag
                ? t("qr-active", {defaultValue: "Active"})
                : state?.showEligibleTag
                    ? t("qr-eligible", {defaultValue: "Ready"})
                    : t("qr-not-eligible", {defaultValue: "Unready"})}
        </StatusTag>
    );
};

// QR invitation sending per registration, the issued registration codes and the QR preview.
export const QrInvitationSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const enumLabel = useEnumLabel();
    const emptyText = useEmptyText();
    const {activeDetail, auth, registrations, registrationCodes, qr, pending, actions} = model;
    const getQrState = qr.getState;

    const invitationColumns = useMemo<TableColumnsType<ParticipantRegistration>>(() => [
        {
            key: "registration",
            title: t("registration", {defaultValue: "Registration"}),
            sorter: textSorter(registration => sortText(
                registration.status,
                registration.isEmailVerified ? "email verified" : "email unverified"
            )),
            render: (_, registration) => (
                <Space direction="vertical" size={4}>
                    <EnumTag value={registration.status}/>
                    {registration.isEmailVerified ? (
                        <StatusTag tone="success">{t("email-verified", {defaultValue: "Verified"})}</StatusTag>
                    ) : (
                        <StatusTag>{t("email-unverified", {defaultValue: "Unverified"})}</StatusTag>
                    )}
                </Space>
            )
        },
        {
            key: "qrInvitation",
            title: t("qr-invitation", {defaultValue: "QR invitation"}),
            sorter: textSorter(registration => {
                const state = getQrState(registration);
                return sortText(
                    state.showActiveTag ? "active" : state.showEligibleTag ? "eligible" : "not eligible",
                    state.activeCodeCount,
                    state.revokedCodeCount
                );
            }),
            render: (_, registration) => {
                const state = getQrState(registration);

                return (
                    <Space direction="vertical" size={4}>
                        <QrInvitationStateTag state={state}/>
                        {state.emailStatus ? (
                            <StatusTag tone={emailDeliveryTone(state.emailStatus)}>
                                {t("email", {defaultValue: "Email"})}: {enumLabel(state.emailStatus)}
                            </StatusTag>
                        ) : null}
                        {state.activeCodeCount > 0 ? (
                            <Typography.Text type="secondary">
                                {t("active-codes-count", {
                                    count: state.activeCodeCount,
                                    defaultValue: "{{count}} active codes"
                                })}
                            </Typography.Text>
                        ) : null}
                        {state.revokedCodeCount > 0 ? (
                            <Typography.Text type="secondary">
                                {t("revoked-codes-count", {
                                    count: state.revokedCodeCount,
                                    defaultValue: "{{count}} revoked codes"
                                })}
                            </Typography.Text>
                        ) : null}
                        <Typography.Text type="secondary">{state.helperText}</Typography.Text>
                        <Button
                            size="small"
                            icon={<MailOutlined/>}
                            loading={pending.invitingQrRegistrationId === registration.id}
                            disabled={!state.canSend}
                            onClick={() => actions.sendQrInvitation(registration)}
                        >
                            {state.actionLabel}
                        </Button>
                    </Space>
                );
            }
        }
    ], [actions, enumLabel, getQrState, pending.invitingQrRegistrationId, t]);

    const codeColumns = useMemo<TableColumnsType<ParticipantRegistrationCode>>(() => [
        {
            key: "code",
            title: t("registration-code", {defaultValue: "Registration code"}),
            sorter: textSorter(code => code.isActive ? "active" : "revoked"),
            render: (_, code) => (
                <StatusTag tone={operationalRegistrationCodeStatusTone(code.isActive)}>
                    {code.isActive
                        ? t("active", {defaultValue: "Active"})
                        : t("revoked", {defaultValue: "Revoked"})}
                </StatusTag>
            )
        },
        {
            key: "issuedAt",
            title: t("issued", {defaultValue: "Issued"}),
            sorter: dateSorter(code => code.issuedAt),
            render: (_, code) => formatLocalDateTime(code.issuedAt, EMPTY_OPERATIONS_VALUE)
        },
        {
            key: "revokedAt",
            title: t("revoked", {defaultValue: "Revoked"}),
            sorter: dateSorter(code => code.revokedAt),
            render: (_, code) => formatLocalDateTime(code.revokedAt, EMPTY_OPERATIONS_VALUE)
        }
    ], [t]);

    return (
        <DetailPanel title={t("qr-invitation", {defaultValue: "QR invitation"})}>
            <Table
                size="small"
                rowKey="id"
                columns={invitationColumns}
                dataSource={registrations}
                pagination={false}
                scroll={{x: 560}}
                locale={{emptyText}}
            />
            <Typography.Title level={4}>
                {t("registration-codes", {defaultValue: "Registration codes"})}
            </Typography.Title>
            <Table
                size="small"
                rowKey="id"
                columns={codeColumns}
                dataSource={registrationCodes}
                pagination={false}
                scroll={{x: 620}}
                locale={{
                    emptyText: t("no-registration-codes", {defaultValue: "No registration codes"})
                }}
            />
            <Modal
                title={t("qr-invitation", {defaultValue: "QR invitation"})}
                open={qr.modalCodeId !== undefined && !!activeDetail}
                onCancel={qr.closeModal}
                footer={null}
                destroyOnHidden
                width={360}
            >
                {qr.modalCodeId && activeDetail ? (
                    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "12px 0"}}>
                        <ParticipantInvitationQrImage
                            key={`${activeDetail.eventId}:${activeDetail.participantId}:${qr.modalCodeId}`}
                            eventId={activeDetail.eventId}
                            participantId={activeDetail.participantId}
                            codeId={qr.modalCodeId}
                            authToken={auth.token}
                            size={280}
                        />
                        <ParticipantRegistrationCodeText
                            eventId={activeDetail.eventId}
                            participantId={activeDetail.participantId}
                            codeId={qr.modalCodeId}
                            authToken={auth.token}
                        />
                    </div>
                ) : null}
            </Modal>
        </DetailPanel>
    );
};
